import { execAdmin } from '@src/apis/reware/utils';

import type {
  YAdjustedRow,
  FetchYAdjustedPricingExecVariables,
  FetchYAdjustedPricingExecResponse,
} from './types';

export interface FetchItemAdjustedPricesArgs {
  partnerId: number;
  page?: number;
  pageSize?: number;
}

export interface FetchItemAdjustedPricesResult {
  count: number;
  page: number;
  pageSize: number;
  rows: YAdjustedRow[];
}

/**
 * Fetches y-adjusted pricing data for a partner.
 * Returns paginated rows from CatalogItems that have a non-null yadjusted_price.
 * Uses offset pagination (page/pageSize), not cursor-based.
 */
export const fetchItemAdjustedPrices = async ({
  partnerId,
  page = 1,
  pageSize = 50,
}: FetchItemAdjustedPricesArgs): Promise<FetchItemAdjustedPricesResult> => {
  const query = `
    query getYadjustedPricing($partnerId: Int!, $page: Int, $pageSize: Int) {
      partner(id: $partnerId) {
        yadjustedPricing(page: $page, pageSize: $pageSize) {
          count
          page
          pageSize
          rows {
            parentSku
            skuSubcode
            color
            yadjustedCode
            price
            yerdlePrice
            yadjustedPrice
            sizeCode
            department
            category
            title
          }
        }
      }
    }
  `;

  const data = await execAdmin<
    FetchYAdjustedPricingExecVariables,
    FetchYAdjustedPricingExecResponse
  >(query, { variables: { partnerId, page, pageSize } });

  const result = data.partner?.yadjustedPricing;
  if (!result) {
    throw new Error(
      `fetchItemAdjustedPrices: unexpected response — partner.yadjustedPricing is ${result === null ? 'null' : 'undefined'} (partnerId=${partnerId})`
    );
  }

  return {
    count: result.count ?? 0,
    page: result.page ?? page,
    pageSize: result.pageSize ?? pageSize,
    rows: (result.rows ?? []).filter((r): r is NonNullable<typeof r> => r != null),
  };
};
